/**
 * Moving and turning obstacles: where saws, moving blocks and rotors are at a tick (see Motion
 * and RotorObject in level/types.ts), and a boss's attacks while they are there (AttackObject).
 * Every position is a pure function of the level's tick — the same in every attempt and for
 * every racer — computed with + − × ÷ and floor only (the sine is a polynomial), so the
 * physics, the level solver and the renderers agree to the bit in V8 and JavaScriptCore.
 * Renderers may pass a fractional tick (between two physics ticks) to draw smooth motion.
 */
import type { AttackObject, LevelObject, Motion, RotorArm } from '../level/types';
import { ROTOR, SAW_HITBOX } from './constants';

/** Taylor coefficients of sin(πx/2), x in [0, 1] (a quarter turn). */
const S1 = 1.5707963267948966;
const S3 = -0.64596409750624617;
const S5 = 0.079692626246167034;
const S7 = -0.0046817541353186866;
const S9 = 0.00016044118478735975;

/** The polynomial at x = 1: dividing by it makes a quarter turn exactly 1. */
const QUARTER = 1 * (S1 + 1 * (S3 + 1 * (S5 + 1 * (S7 + 1 * S9))));

/**
 * sin 2πu for u in turns: the polynomial on a quarter turn, folded (error under 4·10⁻⁶; exactly
 * 0 at whole and half turns, ±1 at the quarters).
 */
export function turnSin(u: number): number {
  let f = u - Math.floor(u);
  let sign = 1;
  if (f >= 0.5) {
    f -= 0.5;
    sign = -1;
  }
  if (f > 0.25) f = 0.5 - f;
  const x = 4 * f;
  const x2 = x * x;
  const v = (x * (S1 + x2 * (S3 + x2 * (S5 + x2 * (S7 + x2 * S9))))) / QUARTER;
  // (0 − v: no −0 at the half turn.)
  return sign < 0 ? 0 - v : v;
}

/** cos 2πu for u in turns (see turnSin). */
export function turnCos(u: number): number {
  return turnSin(u + 0.25);
}

/**
 * Share of its cycle (0 ≤ u < 1) a motion of `period` ticks (its magnitude) with `phase` has done
 * at `tick`; exact for whole ticks.
 */
export function cycleAt(period: number, phase: number, tick: number): number {
  const p = Math.abs(period);
  const t = tick + phase;
  return (t - Math.floor(t / p) * p) / p;
}

/** The most of a cycle a line motion may rest at each end (see Motion.hold). */
export const MAX_HOLD = 0.4;

/**
 * Where along its line a motion is at share u of its cycle, −1 … 1: sin 2πu, or with `hold` > 0
 * resting that share of the cycle at +1 (around u = 0.25) and at −1 (around u = 0.75), easing in
 * and out between them (half a cosine), through 0 at u = 0 and 0.5.
 */
export function lineTravel(u: number, hold = 0): number {
  const rest = Math.min(MAX_HOLD, Math.max(0, hold));
  if (rest <= 0) return turnSin(u);
  // Each move takes `move` of the cycle; v counts from the start of the move up.
  const move = 0.5 - rest;
  let v = u + move / 2;
  v -= Math.floor(v);
  if (v < move) return -turnCos(v / move / 2);
  if (v < move + rest) return 1;
  if (v < 2 * move + rest) return turnCos((v - move - rest) / move / 2);
  return -1;
}

/** A displacement in blocks. */
export interface Offset {
  x: number;
  y: number;
}

/** How far a motion has moved its object from its cell at `tick` (written into `out`). */
export function motionOffset(m: Motion, tick: number, out: Offset): Offset {
  const u = cycleAt(m.period, m.phase, tick);
  if (m.path === 'circle') {
    out.x = m.dx * turnCos(u);
    out.y = m.dy * turnSin(u);
  } else {
    const p = lineTravel(u, m.hold ?? 0);
    out.x = m.dx * p;
    out.y = m.dy * p;
  }
  return out;
}

/** An axis-aligned box (x1 < x2, y1 < y2). */
export interface Bounds {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/**
 * The box an object may occupy at any tick: its cell, widened by its motion's reach (a rotor's
 * cell is what its arms sweep already; an attack's, its way from `from` to `to`). Indices,
 * clearances and camera hints use it.
 */
export function sweptBounds(o: LevelObject): Bounds {
  if (o.type === 'attack') return attackSweep(o);
  const move = (o.type === 'block' || o.type === 'saw') && o.move ? o.move : null;
  const rx = move ? Math.abs(move.dx) : 0;
  const ry = move ? Math.abs(move.dy) : 0;
  return { x1: o.x - rx, y1: o.y - ry, x2: o.x + o.w + rx, y2: o.y + o.h + ry };
}

/** Whether an object moves or turns (its place depends on the tick), or comes and goes. */
export function isMoving(o: LevelObject): boolean {
  return (
    o.type === 'rotor' ||
    o.type === 'attack' ||
    ((o.type === 'block' || o.type === 'saw') && o.move !== undefined)
  );
}

/** What says where an attack is (see AttackObject). */
export type AttackShape = Pick<
  AttackObject,
  'x' | 'y' | 'w' | 'h' | 'from' | 'to' | 'dx' | 'dy' | 'lift' | 'grow' | 'grows'
>;

/**
 * Where a boss's attack is at `tick` (written into `out`); false when it is not there — before
 * its `from`, from its `to` on. Moved along its way (straight, plus the parabola's lift), grown
 * out of its base over its first `grow` ticks. Renderers may pass a fractional tick.
 */
export function attackBox(o: AttackShape, tick: number, out: Bounds): boolean {
  if (!(tick >= o.from && tick < o.to)) return false;
  const u = (tick - o.from) / (o.to - o.from);
  const lift = o.lift ?? 0;
  const x1 = o.x + (o.dx ?? 0) * u;
  let y1 = o.y + (o.dy ?? 0) * u + 4 * lift * u * (1 - u);
  let y2 = y1 + o.h;
  const grow = o.grow ?? 0;
  if (grow > 0) {
    const g = Math.min(1, (tick - o.from + 1) / grow);
    if (o.grows === 'down') y1 = y2 - o.h * g;
    else y2 = y1 + o.h * g;
  }
  out.x1 = x1;
  out.y1 = y1;
  out.x2 = x1 + o.w;
  out.y2 = y2;
  return true;
}

/** The box an attack sweeps on its way (its cell at both ends and at its arc's top or bottom). */
function attackSweep(o: AttackShape): Bounds {
  const dx = o.dx ?? 0;
  const dy = o.dy ?? 0;
  const lift = o.lift ?? 0;
  let low = Math.min(0, dy);
  let high = Math.max(0, dy);
  if (lift !== 0) {
    // y' = dy + 4·lift·(1 − 2u) = 0: the way's turning point, if it comes before its end.
    const u = (dy + 4 * lift) / (8 * lift);
    if (u > 0 && u < 1) {
      const y = dy * u + 4 * lift * u * (1 - u);
      low = Math.min(low, y);
      high = Math.max(high, y);
    }
  }
  return {
    x1: o.x + Math.min(0, dx),
    y1: o.y + low,
    x2: o.x + o.w + Math.max(0, dx),
    y2: o.y + o.h + high,
  };
}

/** The radius within which a saw blade of cell size `size` kills (see SAW_HITBOX). */
export function sawRadius(size: number): number {
  return (size / 2) * SAW_HITBOX;
}

/**
 * The deadly circles along one arm of a rotor whose arms reach `reach` from the pivot: their
 * distances from it (outermost first) and their radius (see ROTOR).
 */
export function rotorSpokes(arm: RotorArm, reach: number): { distances: number[]; radius: number } {
  if (arm === 'mace') {
    return { distances: [Math.max(0, reach - ROTOR.maceInset)], radius: ROTOR.maceRadius };
  }
  const distances: number[] = [];
  for (let d = reach - ROTOR.barInset; d > -1e-9; d -= ROTOR.barStep) {
    distances.push(Math.max(0, d));
  }
  return { distances, radius: ROTOR.barRadius };
}

/**
 * The direction (in turns) arm `k` of a rotor points to at `tick`: the arms evenly spread, arm 0
 * along +x at the turn's start, turning counterclockwise (clockwise for a negative period).
 */
export function rotorTurn(
  period: number,
  phase: number,
  arms: number,
  k: number,
  tick: number,
): number {
  const u = cycleAt(period, phase, tick);
  return (period < 0 ? -u : u) + k / arms;
}
